import InternshipsService from "services/InternshipsService";

const { createAsyncThunk, createSlice } = require("@reduxjs/toolkit");

export const getInternships = createAsyncThunk(
    "internships/getAll",
    async (_, { rejectWithValue }) => {
        try {
            const response = await InternshipsService.getInternships();
            return response.data.data;
        } catch (error) {
            return rejectWithValue(error.response || "Erreur");
        }
    }
);

export const getInternshipById = createAsyncThunk(
    "internships/getInternshipById",
    async (internshipId, { rejectWithValue }) => {
        try {
            const response = await InternshipsService.getInternshipById(
                internshipId
            );
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response || "Erreur");
        }
    }
);

export const createInternship = createAsyncThunk(
    "internships/createInternship",
    async (data, { rejectWithValue }) => {
        try {
            const response = await InternshipsService.createInternship(data);
            return response.data.stage;
        } catch (error) {
            return rejectWithValue(error.response.data.error || "Erreur");
        }
    }
);

export const updateInternshipById = createAsyncThunk(
    "internships/updateInternshipById",
    async ({ internshipId, data }, { rejectWithValue }) => {
        try {
            const response = await InternshipsService.updateInternship(
                internshipId,
                data
            );
            return response.data.data;
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || "Erreur");
        }
    }
);

export const deleteInternshipById = createAsyncThunk(
    "internships/deleteInternshipById",
    async (id, { rejectWithValue }) => {
        try {
            // TODO: this is supposed to return the deleted internship id, but the API returns only a status message
            await InternshipsService.deleteInternshipById(id);
            return id;
        } catch (error) {
            return rejectWithValue(error.response || "Erreur");
        }
    }
);

export const addStudentToInternship = createAsyncThunk(
    "internships/addStudentToInternship",
    async ({ studentId, internshipId }, { rejectWithValue }) => {
        try {
            const response = await InternshipsService.addStudentToInternship(
                internshipId,
                studentId
            );
            return response.data.studentStage;
        } catch (error) {
            return rejectWithValue(error.response || "Erreur");
        }
    }
);

export const removeStudentFromInternship = createAsyncThunk(
    "internships/removeStudentFromInternship",
    async ({ studentStageId, internshipId }, { rejectWithValue }) => {
        try {
            await InternshipsService.removeStudentFromInternship(
                internshipId,
                studentStageId
            );
            return {
                studentStageId,
            };
        } catch (error) {
            return rejectWithValue(error.response || "Erreur");
        }
    }
);

export const getStudentsByInternshipId = createAsyncThunk(
    "internships/getStudentsByInternshipId",
    async (internshipId, { rejectWithValue }) => {
        try {
            const response = await InternshipsService.getStudentsByInternshipId(
                internshipId
            );
            return response.data.data;
        } catch (error) {
            return rejectWithValue(error.response || "Erreur");
        }
    }
);

const initialState = {
    internships: [],
    internship: null,
    loading: false,
    error: null,
};

const InternshipsSlice = createSlice({
    name: "internships",
    initialState,
    reducers: {
        setInternships: (state, { payload }) => {
            state.internships = payload;
        },
        setInternship: (state, { payload }) => {
            state.internship = payload;
        },
        setLoading: (state, { payload }) => {
            state.loading = payload;
        },
        setError: (state, { payload }) => {
            state.error = payload;
        },
    },
    extraReducers: {
        [getInternships.pending]: (state) => {
            state.loading = true;
        },
        [getInternships.fulfilled]: (state, { payload }) => {
            state.internships = payload;
            state.loading = false;
        },
        [getInternships.rejected]: (state, { payload }) => {
            state.error = payload;
            state.loading = false;
        },
        [getInternshipById.pending]: (state) => {
            state.loading = true;
        },
        [getInternshipById.fulfilled]: (state, { payload }) => {
            state.internship = payload;
            state.loading = false;
        },
        [getInternshipById.rejected]: (state, { payload }) => {
            state.loading = false;
        },
        [createInternship.pending]: (state, { payload }) => {
            state.loading = true;
        },
        [createInternship.fulfilled]: (state, { payload }) => {
            state.internships.push(payload);
            state.loading = false;
        },
        [createInternship.rejected]: (state, { payload }) => {
            state.loading = false;
        },
        [updateInternshipById.pending]: (state) => {
            state.loading = true;
        },
        [updateInternshipById.fulfilled]: (state, { payload }) => {
            state.internships = state.internships.map((internship) =>
                internship._id === payload._id ? payload : internship
            );
            state.loading = false;
        },
        [updateInternshipById.rejected]: (state, { payload }) => {
            state.loading = false;
        },
        [deleteInternshipById.pending]: (state) => {
            state.loading = true;
        },
        [deleteInternshipById.fulfilled]: (state, { payload }) => {
            state.internships = state.internships.filter(
                (internship) => internship._id !== payload
            );
            state.loading = false;
        },
        [deleteInternshipById.rejected]: (state, { payload }) => {
            state.loading = false;
        },
        [addStudentToInternship.pending]: (state) => {
            state.loading = true;
        },
        [addStudentToInternship.fulfilled]: (state, { payload }) => {
            state.internship.students.push(payload);
            state.loading = false;
        },
        [addStudentToInternship.rejected]: (state, { payload }) => {
            state.loading = false;
        },
        [removeStudentFromInternship.pending]: (state) => {
            state.loading = true;
        },
        [removeStudentFromInternship.fulfilled]: (state, { payload }) => {
            state.internship.students = state.internship.students.filter(
                (student) => student.studentStageId !== payload.studentStageId
            );
            state.loading = false;
        },
        [removeStudentFromInternship.rejected]: (state, { payload }) => {
            state.loading = false;
        },
        [getStudentsByInternshipId.pending]: (state) => {
            state.loading = true;
        },
        [getStudentsByInternshipId.fulfilled]: (state, { payload }) => {
            state.internship.students = payload;
            state.loading = false;
        },
        [getStudentsByInternshipId.rejected]: (state, { payload }) => {
            state.loading = false;
        },
    },
});

export const { setInternships, setInternship, setLoading, setError } =
    InternshipsSlice.actions;

export default InternshipsSlice.reducer;
